// src/hooks/useRuler.js
import { useEffect, useRef } from 'react';

const PX_PER_INCH  = 66;
const PX_PER_CM    = 66 / 2.54;          // ≈ 25.98 px per cm
const RULER_SIZE   = 28;

const INTERVALS_IN   = [0.125, 0.25, 0.5, 1, 2, 4];
const INTERVALS_CM   = [0.1, 0.2, 0.5, 1, 2, 5];
const MIN_LABEL_PX   = 52;   // inches — labels can be long ("0.125")
const MIN_LABEL_PX_CM = 30;  // cm labels are short ("2"), so allow tighter spacing

const BLEED_IN = 0.125;
const SAFE_IN  = 0.25;

const BG          = '#3a3357';
const TICK_MAJOR  = 'rgba(255,255,255,0.60)';
const TICK_MINOR  = 'rgba(255,255,255,0.25)';
const LABEL_COLOR = 'rgba(255,255,255,0.68)';
const HAIRLINE    = 'rgba(255,80,80,0.90)';
const FOLD_COLOR  = 'rgba(255,100,100,0.70)';
const BORDER      = 'rgba(255,255,255,0.12)';
const FONT        = '9px system-ui,sans-serif';
const BLEED_COLOR = 'rgba(255, 80,  80,  0.15)';
const SAFE_COLOR  = 'rgba(255, 200, 50,  0.12)';
const SEL_COLOR   = 'rgba(100, 180, 255, 0.20)';
const SEL_BORDER  = 'rgba(100, 180, 255, 0.75)';

function pickInterval(zoom, unit) {
  const pxPer    = unit === 'cm' ? PX_PER_CM    : PX_PER_INCH;
  const intervals = unit === 'cm' ? INTERVALS_CM  : INTERVALS_IN;
  const minPx    = unit === 'cm' ? MIN_LABEL_PX_CM : MIN_LABEL_PX;
  const fallback  = unit === 'cm' ? 5              : 4;
  return intervals.find(i => i * pxPer * zoom >= minPx) ?? fallback;
}

function fmtUnit(v, unit) {
  if (Math.abs(v) < 0.0001) return '0';
  if (unit === 'cm') return parseFloat(v.toFixed(2)).toString();
  if (Math.abs(v - Math.round(v)) < 0.0001) return String(Math.round(v));
  return parseFloat(v.toFixed(3)).toString();
}

function fmtCoord(v) {
  return v.toFixed(2);
}

// ─── Bleed / safe-zone bands ─────────────────────────────────────────────────

function drawBandsH(ctx, H, { z, ox, sl, viewW }) {
  const edgeL   = ox - sl;
  const edgeR   = viewW * z + ox - sl;
  const bleedPx = BLEED_IN * PX_PER_INCH * z;
  const safePx  = SAFE_IN  * PX_PER_INCH * z;

  ctx.fillStyle = BLEED_COLOR;
  ctx.fillRect(edgeL,              0, bleedPx,           H);
  ctx.fillRect(edgeR - bleedPx,    0, bleedPx,           H);
  ctx.fillStyle = SAFE_COLOR;
  ctx.fillRect(edgeL + bleedPx,    0, safePx - bleedPx,  H);
  ctx.fillRect(edgeR - safePx,     0, safePx - bleedPx,  H);
}

function drawBandsV(ctx, W, { z, oy, st, faceH }) {
  const edgeT   = oy - st;
  const edgeB   = faceH * z + oy - st;
  const bleedPx = BLEED_IN * PX_PER_INCH * z;
  const safePx  = SAFE_IN  * PX_PER_INCH * z;

  ctx.fillStyle = BLEED_COLOR;
  ctx.fillRect(0, edgeT,           W, bleedPx);
  ctx.fillRect(0, edgeB - bleedPx, W, bleedPx);
  ctx.fillStyle = SAFE_COLOR;
  ctx.fillRect(0, edgeT + bleedPx, W, safePx - bleedPx);
  ctx.fillRect(0, edgeB - safePx,  W, safePx - bleedPx);
}

// ─── Core drawing ────────────────────────────────────────────────────────────
//
// ox / oy: pixel offset from the scroll-area's top-left corner to the canvas
// element's top-left corner (accounting for centering, padding, and scroll).
// All tick positions are computed relative to this offset so 0" lines up with
// the canvas edge, not the scroll-area edge.

function drawHRuler(hEl, { z, sl, ox, faceW, spineW, activeView, mx, selBounds, panX, viewW, unit }) {
  const ctx = hEl.getContext('2d');
  const W = hEl.width, H = hEl.height;
  const pxPer = unit === 'cm' ? PX_PER_CM : PX_PER_INCH;

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);

  drawBandsH(ctx, H, { z, ox, sl, viewW });

  ctx.fillStyle = BORDER;
  ctx.fillRect(0, H - 1, W, 1);

  // Selected object extent highlight
  if (selBounds) {
    const rxL = selBounds.left * z + panX + ox - sl;
    const rxR = (selBounds.left + selBounds.width) * z + panX + ox - sl;
    const cL  = Math.max(0, rxL), cR = Math.min(W, rxR);
    if (cR > cL) {
      ctx.fillStyle = SEL_COLOR;
      ctx.fillRect(cL, 0, cR - cL, H);
      ctx.strokeStyle = SEL_BORDER;
      ctx.lineWidth   = 1;
      if (rxL >= -1 && rxL <= W + 1) {
        ctx.beginPath(); ctx.moveTo(Math.round(rxL) + 0.5, 0); ctx.lineTo(Math.round(rxL) + 0.5, H); ctx.stroke();
      }
      if (rxR >= -1 && rxR <= W + 1) {
        ctx.beginPath(); ctx.moveTo(Math.round(rxR) + 0.5, 0); ctx.lineTo(Math.round(rxR) + 0.5, H); ctx.stroke();
      }
    }
  }

  const interval = pickInterval(z, unit);
  const sub      = interval / 4;
  const startU   = (sl - ox) / z / pxPer;
  const endU     = (sl + W - ox) / z / pxPer;
  let   u        = Math.floor(startU / sub) * sub;

  ctx.font         = FONT;
  ctx.textBaseline = 'top';

  while (u <= endU + sub) {
    const rx = u * pxPer * z + ox - sl;
    if (rx > -1 && rx < W + 1) {
      const isMajor = Math.abs(Math.round(u / interval) * interval - u) < 0.0001;
      const isHalf  = !isMajor && Math.abs(Math.round(u / (interval / 2)) * (interval / 2) - u) < 0.0001;
      const tickH   = isMajor ? H * 0.55 : isHalf ? H * 0.40 : H * 0.22;
      const px      = Math.round(rx) + 0.5;

      ctx.strokeStyle = isMajor ? TICK_MAJOR : TICK_MINOR;
      ctx.lineWidth   = 1;
      ctx.beginPath();
      ctx.moveTo(px, H);
      ctx.lineTo(px, H - tickH);
      ctx.stroke();

      if (isMajor) {
        ctx.fillStyle = LABEL_COLOR;
        ctx.fillText(fmtUnit(u, unit), Math.round(rx) + 2, 2);
      }
    }
    u = parseFloat((u + sub).toFixed(6));
  }

  // Fold-line markers (Full view): foldPx is in canvas-pixel space
  if (activeView === 'full') {
    ctx.strokeStyle = FOLD_COLOR;
    ctx.lineWidth   = 1;
    ctx.setLineDash([2, 2]);
    for (const foldPx of [faceW, faceW + spineW]) {
      const rx = foldPx * z + ox - sl;
      if (rx < 0 || rx > W) continue;
      ctx.beginPath();
      ctx.moveTo(Math.round(rx) + 0.5, 4);
      ctx.lineTo(Math.round(rx) + 0.5, H);
      ctx.stroke();
    }
    ctx.setLineDash([]);
  }

  // Cursor hairline
  if (mx >= 0 && mx <= W) {
    ctx.strokeStyle = HAIRLINE;
    ctx.lineWidth   = 1;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(Math.round(mx) + 0.5, 0);
    ctx.lineTo(Math.round(mx) + 0.5, H);
    ctx.stroke();
  }
}

function drawVRuler(vEl, { z, st, oy, faceH, my, selBounds, panY, unit }) {
  const ctx = vEl.getContext('2d');
  const W = vEl.width, H = vEl.height;
  const pxPer = unit === 'cm' ? PX_PER_CM : PX_PER_INCH;

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);

  drawBandsV(ctx, W, { z, oy, st, faceH });

  ctx.fillStyle = BORDER;
  ctx.fillRect(W - 1, 0, 1, H);

  // Selected object extent highlight
  if (selBounds) {
    const ryT = selBounds.top * z + panY + oy - st;
    const ryB = (selBounds.top + selBounds.height) * z + panY + oy - st;
    const cT  = Math.max(0, ryT), cB = Math.min(H, ryB);
    if (cB > cT) {
      ctx.fillStyle = SEL_COLOR;
      ctx.fillRect(0, cT, W, cB - cT);
      ctx.strokeStyle = SEL_BORDER;
      ctx.lineWidth   = 1;
      if (ryT >= -1 && ryT <= H + 1) {
        ctx.beginPath(); ctx.moveTo(0, Math.round(ryT) + 0.5); ctx.lineTo(W, Math.round(ryT) + 0.5); ctx.stroke();
      }
      if (ryB >= -1 && ryB <= H + 1) {
        ctx.beginPath(); ctx.moveTo(0, Math.round(ryB) + 0.5); ctx.lineTo(W, Math.round(ryB) + 0.5); ctx.stroke();
      }
    }
  }

  const interval = pickInterval(z, unit);
  const sub      = interval / 4;
  const startU   = (st - oy) / z / pxPer;
  const endU     = (st + H - oy) / z / pxPer;
  let   u        = Math.floor(startU / sub) * sub;

  ctx.font = FONT;

  while (u <= endU + sub) {
    const ry = u * pxPer * z + oy - st;
    if (ry > -1 && ry < H + 1) {
      const isMajor = Math.abs(Math.round(u / interval) * interval - u) < 0.0001;
      const isHalf  = !isMajor && Math.abs(Math.round(u / (interval / 2)) * (interval / 2) - u) < 0.0001;
      const tickW   = isMajor ? W * 0.55 : isHalf ? W * 0.40 : W * 0.22;
      const py      = Math.round(ry) + 0.5;

      ctx.strokeStyle = isMajor ? TICK_MAJOR : TICK_MINOR;
      ctx.lineWidth   = 1;
      ctx.beginPath();
      ctx.moveTo(W, py);
      ctx.lineTo(W - tickW, py);
      ctx.stroke();

      if (isMajor) {
        ctx.save();
        ctx.fillStyle    = LABEL_COLOR;
        ctx.textBaseline = 'bottom';
        ctx.translate(W - 2, Math.round(ry) - 1);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(fmtUnit(u, unit), 0, 0);
        ctx.restore();
      }
    }
    u = parseFloat((u + sub).toFixed(6));
  }

  // Cursor hairline
  if (my >= 0 && my <= H) {
    ctx.strokeStyle = HAIRLINE;
    ctx.lineWidth   = 1;
    ctx.beginPath();
    ctx.moveTo(0, Math.round(my) + 0.5);
    ctx.lineTo(W, Math.round(my) + 0.5);
    ctx.stroke();
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useRuler({ hRulerRef, vRulerRef, crosshairRef, coordReadoutRef, scrollRef, canvas, zoom, activeView, faceDims, showRulers, unit = 'in' }) {
  const mouse     = useRef({ x: -1, y: -1 });
  const drawRef   = useRef(null);
  const selBounds = useRef(null);

  drawRef.current = () => {
    if (!showRulers || !canvas) return;
    const sc = scrollRef.current;
    if (!sc) return;

    const vpt  = canvas.viewportTransform;
    const z    = vpt ? vpt[0] : zoom;
    const panX = vpt ? vpt[4] : 0;
    const panY = vpt ? vpt[5] : 0;
    const sl   = sc.scrollLeft;
    const st   = sc.scrollTop;
    const { faceW, spineW, faceH } = faceDims;
    const { x: mx, y: my } = mouse.current;

    // Measure where the Fabric canvas sits inside the scroll area.
    // This accounts for centering, padding, and any margin in the layout.
    let ox = 0, oy = 0;
    const upperEl = canvas.upperCanvasEl;
    if (upperEl) {
      const sr = sc.getBoundingClientRect();
      const cr = upperEl.getBoundingClientRect();
      ox = cr.left - sr.left + sl;
      oy = cr.top  - sr.top  + st;
    }

    const viewW = activeView === 'full'  ? 2 * faceW + spineW
                : activeView === 'spine' ? spineW
                : faceW;

    const sb = selBounds.current;

    if (hRulerRef.current) drawHRuler(hRulerRef.current, { z, sl, ox, faceW, spineW, activeView, mx, selBounds: sb, panX, viewW, unit });
    if (vRulerRef.current) drawVRuler(vRulerRef.current, { z, st, oy, faceH, my, selBounds: sb, panY, unit });

    // Coordinate readout
    const readout = coordReadoutRef?.current;
    if (readout) {
      if (mx >= 0) {
        const pxPer = unit === 'cm' ? PX_PER_CM : PX_PER_INCH;
        const ux = (mx + sl - ox) / z / pxPer;
        const uy = (my + st - oy) / z / pxPer;
        readout.textContent = unit === 'cm'
          ? `X: ${fmtCoord(ux)} cm  Y: ${fmtCoord(uy)} cm`
          : `X: ${fmtCoord(ux)}"  Y: ${fmtCoord(uy)}"`;
        const offX = mx + 16 + 80 < sc.clientWidth  ? mx + 16 : mx - 88;
        const offY = my - 28 > 0                    ? my - 28 : my + 10;
        readout.style.left    = `${offX}px`;
        readout.style.top     = `${offY}px`;
        readout.style.display = 'block';
      } else {
        readout.style.display = 'none';
      }
    }
  };

  // Resize ruler canvas buffers to match scroll container viewport
  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc || !showRulers) return;
    const sync = () => {
      if (hRulerRef.current) hRulerRef.current.width  = sc.clientWidth;
      if (vRulerRef.current) vRulerRef.current.height = sc.clientHeight;
      drawRef.current();
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(sc);
    return () => ro.disconnect();
  }, [showRulers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll → redraw
  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc || !showRulers) return;
    const onScroll = () => drawRef.current();
    sc.addEventListener('scroll', onScroll, { passive: true });
    return () => sc.removeEventListener('scroll', onScroll);
  }, [showRulers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Zoom / view / dims / unit → redraw
  useEffect(() => {
    drawRef.current?.();
  }, [zoom, activeView, faceDims, showRulers, unit]);

  // Fabric canvas selection events → update selBounds ref and redraw
  useEffect(() => {
    if (!canvas) return;
    const update = () => {
      const active = canvas.getActiveObject?.();
      if (active && typeof active.getBoundingRect === 'function') {
        selBounds.current = active.getBoundingRect();
      } else {
        selBounds.current = null;
      }
      drawRef.current?.();
    };
    const clear = () => { selBounds.current = null; drawRef.current?.(); };

    canvas.on('selection:created', update);
    canvas.on('selection:updated', update);
    canvas.on('selection:cleared', clear);
    canvas.on('object:modified',   update);
    canvas.on('object:moving',     update);
    canvas.on('object:scaling',    update);
    canvas.on('object:rotating',   update);
    return () => {
      canvas.off('selection:created', update);
      canvas.off('selection:updated', update);
      canvas.off('selection:cleared', clear);
      canvas.off('object:modified',   update);
      canvas.off('object:moving',     update);
      canvas.off('object:scaling',    update);
      canvas.off('object:rotating',   update);
    };
  }, [canvas]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mouse → cursor hairline on rulers + crosshair overlay on canvas
  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return;

    const moveCrosshair = (mx, my) => {
      const ch = crosshairRef?.current;
      if (!ch) return;
      const [hLine, vLine] = ch.children;
      if (hLine) hLine.style.transform = my >= 0 ? `translateY(${my}px)` : 'translateY(-9999px)';
      if (vLine) vLine.style.transform = mx >= 0 ? `translateX(${mx}px)` : 'translateX(-9999px)';
    };

    const onMove = (e) => {
      const r = sc.getBoundingClientRect();
      const mx = e.clientX - r.left;
      const my = e.clientY - r.top;
      mouse.current = { x: mx, y: my };
      moveCrosshair(mx, my);
      drawRef.current();
    };
    const onLeave = () => {
      mouse.current = { x: -1, y: -1 };
      moveCrosshair(-1, -1);
      drawRef.current();
    };

    sc.addEventListener('mousemove', onMove);
    sc.addEventListener('mouseleave', onLeave);
    return () => {
      sc.removeEventListener('mousemove', onMove);
      sc.removeEventListener('mouseleave', onLeave);
    };
  }, [showRulers]); // eslint-disable-line react-hooks/exhaustive-deps
}

export { RULER_SIZE };
